import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  createClanShare,
  getClanFeed,
  getJoinCode,
  listClanEvents,
  listClanInvites,
  listClanMembers,
  removeClanMember,
  reportClanShare,
  revokeClanInvite,
  rotateJoinCode,
  sendClanInvite,
  updateClanMember,
  type ReportReason,
} from "@/api/clans";
import { createCoop } from "@/api/coop";
import { listQuestionSets } from "@/api/question-sets";
import { searchUsers } from "@/api/users";
import type { ClanEventRead, ClanMemberRead, ClanRead, ClanRole, ClanShareRead } from "@/api/community-types";
import { Alert, Button, Input, Spinner, cx } from "@/components/ui";
import { Avatar } from "@/components/avatar";
import { ClassIcon, roleLabel } from "./class-theme";
import { EditClassModal } from "./edit-class-modal";
import { ChatRoom } from "./chat-room";
import { RulesPanel } from "./rules-panel";
import { SchedulePanel } from "./schedule-panel";
import { PublicProfileModal } from "@/profile/public-profile-modal";
import { useTranslation } from "react-i18next";

function timeAgo(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

/** Human-readable summary of one activity event. */
function eventText(ev: ClanEventRead, t: (s: string, o?: Record<string, unknown>) => string): string {
  const who = ev.actor_display_name ?? t("Someone");
  const p = ev.payload ?? {};
  switch (ev.event_kind) {
    case "set_shared":
      return t("{{who}} shared {{title}}", { who, title: (p.title as string) || t("a set") });
    case "member_joined":
      return t("{{who}} joined the class", { who });
    case "homework_posted":
      return t("{{who}} posted homework: {{title}}", { who, title: (p.title as string) || "" });
    default:
      return t("{{who}} did something", { who });
  }
}

function eventEmoji(kind: string): string {
  switch (kind) {
    case "set_shared": return "📘";
    case "member_joined": return "👋";
    case "homework_posted": return "📌";
    default: return "•";
  }
}

/** The class activity feed — "what classmates did". */
function ActivityPanel({ clanId }: { clanId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["clan-events", clanId],
    queryFn: () => listClanEvents(clanId),
    enabled: !!clanId,
  });
  if (isLoading) {
    return (
      <div className="flex justify-center py-10 text-brand-600">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  const events = data ?? [];
  if (events.length === 0) {
    return <p className="py-8 text-center text-sm text-ink-faint">{t("No activity yet. As classmates share, join, and practice, it'll show up here.")}</p>;
  }
  return (
    <div className="mx-auto max-w-xl space-y-2">
      {events.map((ev) => (
        <div key={ev.id} className="card flex items-center gap-3 p-3">
          <Avatar url={ev.actor_avatar_url} name={ev.actor_display_name ?? "?"} size={32} />
          <p className="min-w-0 flex-1 text-sm text-ink">
            <span className="mr-1">{eventEmoji(ev.event_kind)}</span>
            {eventText(ev, t)}
          </p>
          <span className="shrink-0 text-xs text-ink-faint">{timeAgo(ev.created_at)}</span>
        </div>
      ))}
    </div>
  );
}

const REPORT_REASONS: { value: ReportReason; label: string }[] = [
  { value: "spam", label: "Spam" },
  { value: "sexual", label: "Sexual content" },
  { value: "violence", label: "Violence" },
  { value: "hate", label: "Hate speech" },
  { value: "other", label: "Copyright / other" },
];

/** Small "report this share" affordance — records a flag for the Teacher. */
function ReportShareButton({ shareId }: { shareId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const mut = useMutation({
    mutationFn: (reason: ReportReason) => reportClanShare(shareId, reason),
    onSuccess: () => {
      setOpen(false);
      setDone(true);
    },
  });
  if (done) return <span className="text-xs text-ink-faint">{t("Reported")}</span>;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="px-1 text-ink-faint hover:text-red-600"
        aria-label={t("Report")}
        title={t("Report")}
      >
        ⚑
      </button>
      {open && (
        <div className="absolute right-0 top-7 z-10 w-44 rounded-xl border border-black/10 bg-white p-1 shadow-lg">
          {REPORT_REASONS.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => mut.mutate(r.value)}
              className="block w-full rounded-lg px-3 py-1.5 text-left text-sm text-ink hover:bg-black/5"
            >
              {t(r.label)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ShareRow({
  share,
  onStartCoop,
  coopPending,
}: {
  share: ClanShareRead;
  onStartCoop?: () => void;
  coopPending?: boolean;
}) {
  const { t } = useTranslation();
  const body = (
    <>
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-base">
        {share.resource_type === "question_set" ? "📘" : "📎"}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-ink">{share.resource_title || t("Shared resource")}</p>
        <p className="text-xs text-ink-faint">
          {t("by {{name}}", { name: share.shared_by_display_name })} · {timeAgo(share.shared_at)}
        </p>
      </div>
    </>
  );
  if (share.resource_type === "question_set") {
    return (
      <div className="card flex items-center gap-2 p-3.5">
        <Link to={`/app/library/${share.resource_id}`} className="flex min-w-0 flex-1 items-center gap-3">
          {body}
        </Link>
        {onStartCoop && (
          <Button size="sm" variant="secondary" onClick={onStartCoop} loading={coopPending}>
            {t("Co-op")}
          </Button>
        )}
        <ReportShareButton shareId={share.id} />
      </div>
    );
  }
  return (
    <div className="card flex items-center gap-3 p-3.5">
      {body}
      <ReportShareButton shareId={share.id} />
    </div>
  );
}

export function ClanDetailPage() {
  const { t } = useTranslation();
  const { clanId = "" } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [coopError, setCoopError] = useState<string | null>(null);

  const coopMut = useMutation({
    mutationFn: (shareId: string) => createCoop(clanId, shareId),
    onSuccess: (s) => navigate(`/app/coop/${s.id}`),
    onError: (e) => setCoopError(e instanceof Error ? e.message : t("Couldn't start co-op.")),
  });

  const feedQuery = useQuery({
    queryKey: ["clan-feed", clanId],
    queryFn: () => getClanFeed(clanId),
    enabled: !!clanId,
  });
  const membersQuery = useQuery({
    queryKey: ["clan-members", clanId],
    queryFn: () => listClanMembers(clanId),
    enabled: !!clanId,
  });

  const clan = feedQuery.data?.clan;
  const canManage = clan?.my_role === "owner" || clan?.my_role === "admin";
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState<"members" | "schedule" | "activity" | "chat" | "rules">("members");
  const [viewingUserId, setViewingUserId] = useState<string | null>(null);

  return (
    <div className="mx-auto max-w-3xl animate-fade-up">
      <Link to="/app/community" className="text-sm text-brand-600 hover:underline">
        ← {t("Community")}
      </Link>

      {feedQuery.isLoading && (
        <div className="flex justify-center py-20 text-brand-600">
          <Spinner className="h-7 w-7" />
        </div>
      )}
      {feedQuery.isError && (
        <div className="mt-6">
          <Alert>{feedQuery.error instanceof Error ? feedQuery.error.message : t("Class not found.")}</Alert>
        </div>
      )}

      {clan && (
        <>
          <div className="mt-4 flex items-start gap-4">
            <ClassIcon emoji={clan.icon_emoji} color={clan.color} size={56} />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold tracking-tight">{clan.name}</h1>
                {canManage && (
                  <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
                    {t("Edit")}
                  </Button>
                )}
              </div>
              <p className="mt-0.5 text-ink-faint">
                {t("{{count}} members", { count: clan.member_count })}
                {clan.my_role && ` · ${roleLabel(clan.my_role)}`}
                {` · ${(clan.join_policy ?? "open") === "invite" ? t("Invite-only") : t("Open")}`}
              </p>
              {clan.description && <p className="mt-1 text-sm text-ink-faint">{clan.description}</p>}
            </div>
          </div>
          {editing && (
            <EditClassModal clan={clan} onClose={() => setEditing(false)} onSaved={() => {}} />
          )}

          <div className="mt-8">
            <div className="inline-flex rounded-xl bg-black/5 p-1">
              {(["members", "schedule", "activity", "chat", "rules"] as const).map((k) => (
                <button
                  key={k}
                  onClick={() => setTab(k)}
                  className={cx(
                    "rounded-lg px-4 py-1.5 text-sm font-semibold transition",
                    tab === k ? "bg-white text-brand-700 shadow-soft" : "text-ink-faint hover:text-ink",
                  )}
                >
                  {k === "members"
                    ? t("Members")
                    : k === "schedule"
                      ? t("Schedule")
                      : k === "activity"
                        ? t("Activity")
                        : k === "chat"
                          ? t("Chat")
                          : t("Rules")}
                </button>
              ))}
            </div>

            <div className="mt-5">
              {tab === "members" && (
                <div className="grid gap-8 md:grid-cols-3">
                  <div className="md:col-span-2">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-faint">
                      {t("Members")}
                    </h2>
                    <div className="mt-3 space-y-3">
                      {clan &&
                        membersQuery.data?.map((m) => (
                          <MemberRow
                            key={m.user_id}
                            clan={clan}
                            member={m}
                            onOpenProfile={setViewingUserId}
                            onChanged={() => qc.invalidateQueries({ queryKey: ["clan-members", clanId] })}
                          />
                        ))}
                    </div>
                    {canManage && (
                      <>
                        <AddMemberForm clanId={clanId} />
                        <PendingInvites clanId={clanId} />
                      </>
                    )}
                  </div>

                  <div>
                    {canManage && <ShareJoinLink clanId={clanId} />}
                    <h2 className="mt-6 text-sm font-semibold uppercase tracking-wide text-ink-faint">
                      {t("Shared sets")}
                    </h2>
                    {canManage && (
                      <ShareSetForm
                        clanId={clanId}
                        onShared={() => qc.invalidateQueries({ queryKey: ["clan-feed", clanId] })}
                      />
                    )}
                    {coopError && (
                      <div className="mt-3">
                        <Alert>{coopError}</Alert>
                      </div>
                    )}
                    <div className="mt-3 space-y-2">
                      {feedQuery.data?.shares.length === 0 && (
                        <p className="text-sm text-ink-faint">{t("Nothing shared yet.")}</p>
                      )}
                      {feedQuery.data?.shares.map((s) => (
                        <ShareRow
                          key={s.id}
                          share={s}
                          onStartCoop={() => {
                            setCoopError(null);
                            coopMut.mutate(s.id);
                          }}
                          coopPending={coopMut.isPending && coopMut.variables === s.id}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {tab === "activity" && <ActivityPanel clanId={clanId} />}

              {tab === "chat" &&
                (clan.chat_room_id ? (
                  <ChatRoom roomId={clan.chat_room_id} />
                ) : (
                  <p className="text-sm text-ink-faint">{t("Chat unavailable for this class.")}</p>
                ))}

              {tab === "schedule" && <SchedulePanel clanId={clanId} canManage={canManage} />}

              {tab === "rules" && <RulesPanel clanId={clanId} canManage={canManage} />}
            </div>
          </div>
        </>
      )}

      {viewingUserId && (
        <PublicProfileModal userId={viewingUserId} onClose={() => setViewingUserId(null)} />
      )}
    </div>
  );
}

function MenuItem({
  children,
  onClick,
  danger,
}: {
  children: ReactNode;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "block w-full rounded-lg px-3 py-1.5 text-left text-sm hover:bg-black/5",
        danger ? "text-red-600" : "text-ink",
      )}
    >
      {children}
    </button>
  );
}

/** A class member row with the role/title/remove management menu. Role changes
 *  are owner-only (the backend enforces it); title is admin+. Mirrors iOS
 *  ClanMembersTab. */
function MemberRow({
  clan,
  member,
  onChanged,
  onOpenProfile,
}: {
  clan: ClanRead;
  member: ClanMemberRead;
  onChanged: () => void;
  onOpenProfile: (userId: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const isOwner = clan.my_role === "owner";
  const canManage = isOwner || clan.my_role === "admin";
  const isClanOwnerRow = member.role === "owner" || member.user_id === clan.owner_user_id;

  const mut = useMutation({
    mutationFn: (p: { role?: ClanRole; title?: string | null }) =>
      updateClanMember(clan.id, member.user_id, p),
    onSuccess: () => {
      setOpen(false);
      onChanged();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("Failed.")),
  });
  const removeMut = useMutation({
    mutationFn: () => removeClanMember(clan.id, member.user_id),
    onSuccess: () => {
      setOpen(false);
      onChanged();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("Failed.")),
  });

  return (
    <div className="relative flex items-center gap-2 text-sm">
      <button
        type="button"
        onClick={() => onOpenProfile(member.user_id)}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        <Avatar url={member.avatar_url} name={member.display_name} size={32} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-ink">{member.display_name}</span>
            {member.role !== "member" && (
              <span className="rounded-full bg-black/5 px-1.5 py-0.5 text-[10px] font-semibold text-ink-soft">
                {roleLabel(member.role)}
              </span>
            )}
          </div>
          {member.title && <p className="truncate text-xs text-ink-faint">{member.title}</p>}
          {err && <p className="text-[10px] text-red-600">{err}</p>}
        </div>
      </button>
      {canManage && !isClanOwnerRow && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="px-1 text-lg leading-none text-ink-faint hover:text-ink"
          aria-label={t("Member actions")}
        >
          ⋯
        </button>
      )}
      {open && (
        <div className="absolute right-0 top-8 z-10 w-52 rounded-xl border border-black/10 bg-white p-1 shadow-lg">
          {isOwner && member.role === "member" && (
            <MenuItem onClick={() => mut.mutate({ role: "admin" })}>
              {t("Make Teaching Assistant")}
            </MenuItem>
          )}
          {isOwner && member.role === "admin" && (
            <MenuItem onClick={() => mut.mutate({ role: "member" })}>{t("Make Student")}</MenuItem>
          )}
          {isOwner && (
            <MenuItem
              onClick={() => {
                if (window.confirm(t("Transfer the Teacher role to {{name}}?", { name: member.display_name })))
                  mut.mutate({ role: "owner" });
              }}
            >
              {t("Transfer Teacher role")}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              const v = window.prompt(t("Title (e.g. Math lead)"), member.title ?? "");
              if (v !== null) mut.mutate({ title: v });
            }}
          >
            {t("Set title…")}
          </MenuItem>
          <MenuItem danger onClick={() => removeMut.mutate()}>
            {t("Remove")}
          </MenuItem>
        </div>
      )}
    </div>
  );
}

function ShareSetForm({ clanId, onShared }: { clanId: string; onShared: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [setId, setSetId] = useState("");
  const [disclaimer, setDisclaimer] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const setsQuery = useQuery({ queryKey: ["question-sets"], queryFn: () => listQuestionSets(false), enabled: open });

  const selectedSet = setsQuery.data?.find((s) => s.id === setId);
  // §14.5: only sourced sets require the copyright attestation (matches iOS).
  const sourced = !!selectedSet?.has_source_material;

  const shareMut = useMutation({
    mutationFn: () =>
      createClanShare(clanId, {
        resource_type: "question_set",
        resource_id: setId,
        source_material_disclaimer: sourced ? disclaimer : false,
      }),
    onSuccess: () => {
      setOpen(false);
      setSetId("");
      setDisclaimer(false);
      onShared();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("Couldn't share.")),
  });

  if (!open) {
    return (
      <Button size="sm" variant="secondary" className="mt-3" onClick={() => setOpen(true)}>
        {t("Share one of your sets")}
      </Button>
    );
  }

  return (
    <div className="card mt-3 space-y-3 p-4">
      {err && <Alert>{err}</Alert>}
      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-ink-soft">{t("Choose a set")}</span>
        <select
          value={setId}
          onChange={(e) => setSetId(e.target.value)}
          className="w-full rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
        >
          <option value="">{t("Select…")}</option>
          {setsQuery.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title}
            </option>
          ))}
        </select>
      </label>
      {sourced && (
        <div className="rounded-xl border border-amber-300/60 bg-amber-50 p-3">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-amber-900">
            <span aria-hidden>🛡️</span>
            {t("Source material attestation")}
          </div>
          <label className="mt-2 flex items-start gap-2 text-sm text-ink-soft">
            <input
              type="checkbox"
              checked={disclaimer}
              onChange={(e) => setDisclaimer(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-black/20 text-brand-600"
            />
            <span>
              <span className="font-medium text-ink">{t("I have rights to redistribute this material.")}</span>{" "}
              {t("This set was built from material you uploaded (a PDF, file, or pasted text). Confirm you're allowed to share it with your class before posting.")}
            </span>
          </label>
        </div>
      )}
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={!setId || (sourced && !disclaimer)}
          loading={shareMut.isPending}
          onClick={() => { setErr(null); shareMut.mutate(); }}
        >
          {t("Share")}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          {t("Cancel")}
        </Button>
      </div>
    </div>
  );
}

/** Search a user and send them a class invite. They must accept before they
 *  join — no direct add. Mirrors iOS AddMemberSheet. */
function AddMemberForm({ clanId }: { clanId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [submittedQ, setSubmittedQ] = useState("");
  const [role, setRole] = useState<"member" | "admin">("member");
  const [invitedId, setInvitedId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const searchQuery = useQuery({
    queryKey: ["user-search", submittedQ],
    queryFn: () => searchUsers(submittedQ),
    enabled: submittedQ.length >= 2,
  });

  const inviteMut = useMutation({
    mutationFn: (userId: string) => sendClanInvite(clanId, { user_id: userId, role }),
    onSuccess: (inv) => {
      setInvitedId(inv.invitee_user_id);
      setErr(null);
      qc.invalidateQueries({ queryKey: ["clan-invites", clanId] });
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("Couldn't send invite.")),
  });

  return (
    <div className="mt-4 border-t border-black/5 pt-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">{t("Invite a member")}</p>
      <div className="flex gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("Search by name or @username")}
          className="text-sm"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as "member" | "admin")}
          className="rounded-xl border border-black/10 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
        >
          <option value="member">{t("Student")}</option>
          <option value="admin">{t("Teaching Assistant")}</option>
        </select>
        <Button size="sm" onClick={() => setSubmittedQ(q.trim())} disabled={q.trim().length < 2}>
          {t("Search")}
        </Button>
      </div>
      {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
      {searchQuery.data && (
        <div className="mt-2 space-y-1">
          {searchQuery.data.length === 0 && <p className="text-xs text-ink-faint">{t("No users found.")}</p>}
          {searchQuery.data.map((u) => (
            <div key={u.user_id} className="flex items-center gap-2 text-sm">
              <span className="truncate text-ink">{u.display_name}</span>
              {u.username && <span className="text-xs text-ink-faint">@{u.username}</span>}
              {invitedId === u.user_id ? (
                <span className="ml-auto text-xs font-medium text-brand-600">{t("Invited ✓")}</span>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  className="ml-auto"
                  loading={inviteMut.isPending && inviteMut.variables === u.user_id}
                  onClick={() => inviteMut.mutate(u.user_id)}
                >
                  {t("Invite")}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Pending invites for this class — admins can see who's been invited and
 *  revoke an outstanding invite. */
function PendingInvites({ clanId }: { clanId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const invitesQuery = useQuery({
    queryKey: ["clan-invites", clanId],
    queryFn: () => listClanInvites(clanId),
    enabled: !!clanId,
  });
  const revokeMut = useMutation({
    mutationFn: (inviteId: string) => revokeClanInvite(clanId, inviteId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clan-invites", clanId] }),
  });

  const invites = invitesQuery.data ?? [];
  if (invites.length === 0) return null;

  return (
    <div className="mt-4 border-t border-black/5 pt-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
        {t("Pending invites")}
      </p>
      <div className="space-y-1.5">
        {invites.map((inv) => (
          <div key={inv.id} className="flex items-center gap-2 text-sm">
            <span className="truncate text-ink">{inv.invitee_display_name}</span>
            {inv.role !== "member" && (
              <span className="rounded-full bg-black/5 px-1.5 py-0.5 text-[10px] font-semibold text-ink-soft">
                {roleLabel(inv.role)}
              </span>
            )}
            <span className="ml-auto text-xs text-ink-faint">{t("Awaiting acceptance")}</span>
            <button
              type="button"
              onClick={() => revokeMut.mutate(inv.id)}
              className="px-1 text-ink-faint hover:text-red-600"
              aria-label={t("Revoke invite")}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Shareable join link + QR. Anyone with the code joins instantly. */
function ShareJoinLink({ clanId }: { clanId: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const codeQuery = useQuery({
    queryKey: ["clan-join-code", clanId],
    queryFn: () => getJoinCode(clanId),
    enabled: !!clanId,
  });
  const rotateMut = useMutation({
    mutationFn: () => rotateJoinCode(clanId),
    onSuccess: () => codeQuery.refetch(),
  });

  const jc = codeQuery.data;

  return (
    <div className="card p-4">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-faint">{t("Invite link")}</h2>
      <p className="mt-1 text-xs text-ink-faint">
        {t("Anyone with this link or QR code can join the class instantly.")}
      </p>
      {codeQuery.isLoading && (
        <div className="flex justify-center py-6 text-brand-600">
          <Spinner className="h-5 w-5" />
        </div>
      )}
      {jc && (
        <div className="mt-3 flex flex-col items-center gap-3">
          <div className="rounded-xl bg-white p-2 shadow-soft">
            <QRCodeSVG value={jc.link} size={132} />
          </div>
          <code className="rounded-lg bg-black/5 px-2 py-1 text-xs font-semibold tracking-widest text-ink">
            {jc.code}
          </code>
          <div className="flex w-full gap-2">
            <Button
              size="sm"
              variant="secondary"
              className="flex-1"
              onClick={() => {
                navigator.clipboard?.writeText(jc.link);
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? t("Copied ✓") : t("Copy link")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              loading={rotateMut.isPending}
              onClick={() => {
                if (window.confirm(t("Reset the link? The old link and QR code will stop working.")))
                  rotateMut.mutate();
              }}
            >
              {t("Reset")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
